import {
  ArrowBack,
  CheckCircle,
  CloudUpload,
  Delete,
  Download,
  Visibility,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../components/layout/Header";
import FileDropzone from "../components/ui/FileDropzone";
import { useSelectedChild } from "../contexts/AppStateContext";
import { usePhotoUpload } from "../hooks/usePhotoUpload";

const PhotoUpload: React.FC = () => {
  const { centerId, childId, folderId } = useParams<{
    centerId: string;
    childId: string;
    folderId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedChild = useSelectedChild();
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [folderName, setFolderName] = useState<string>("");

  // Use the real photo upload hook
  const {
    selectedChild: hookSelectedChild,
    uploadedFiles,
    selectedDate,
    uploadProgress,
    uploading,
    loading,
    error,
    success,
    setSelectedChild: setHookSelectedChild,
    setSelectedDate,
    setError,
    setSuccess,
    onDrop,
    removeFile,
    uploadFiles,
  } = usePhotoUpload();

  // Set the selected child from URL params when component mounts
  useEffect(() => {
    if (childId && hookSelectedChild !== childId) {
      setHookSelectedChild(childId);
    }
  }, [childId, hookSelectedChild, setHookSelectedChild]);

  // Resolve folder name from location state or use folderId as fallback
  useEffect(() => {
    if (location.state?.folderName) {
      setFolderName(location.state.folderName);
    } else {
      // Fallback to folderId (UUID value)
      setFolderName(folderId || "");
    }
  }, [location.state, folderId]);

  // Convert uploaded files to photo format for display
  const uploadedPhotos = uploadedFiles.map((file) => ({
    id: file.id,
    name: file.file.name,
    url: file.preview,
    uploadDate: selectedDate,
    size: (file.file.size / (1024 * 1024)).toFixed(1) + " MB",
    uploaded: file.uploaded,
    uploading: uploadProgress[file.id]?.uploading || false,
    progress: uploadProgress[file.id]?.progress || 0,
    downloadURL: file.downloadURL,
  }));

  const handleBack = () => {
    navigate(`/center/${centerId}/child/${childId}/folder/${folderId}`, {
      state: { folderName: folderName, folderId: folderId },
    });
  };

  const handleDeletePhoto = (photoId: string) => {
    removeFile(photoId);
  };

  const handleUpload = async () => {
    await uploadFiles(folderName, folderId);
    // All files uploaded successfully
    setTimeout(() => {
      handleBack();
    }, 1500);
  };

  const handleDownloadPhoto = (photo: any) => {
    if (photo.downloadURL) {
      window.open(photo.downloadURL, "_blank");
    } else if (photo.url) {
      const link = document.createElement("a");
      link.href = photo.url;
      link.download = photo.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <Header />
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Header />

      {/* Breadcrumb */}
      <Box sx={{ p: 3, pb: 0 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Photo Folder
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Upload Photos
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {selectedChild?.name || "Child Name"} • Add photos to this folder
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Upload Controls */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              type="date"
              label="Photo Date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              size="small"
              sx={{ width: 160 }}
            />
            {uploadedFiles.length > 0 && (
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={uploading}
                startIcon={
                  uploading ? <CircularProgress size={16} /> : <CloudUpload />
                }
              >
                {uploading
                  ? "Uploading..."
                  : `Upload ${uploadedFiles.length} Photos`}
              </Button>
            )}
          </Box>
        </Box>

        {/* File Dropzone */}
        <Box sx={{ mb: 3 }}>
          <FileDropzone onDrop={onDrop} />
        </Box>

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Uploading photos...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* Photo Grid - Preview of files to upload */}
        {uploadedPhotos.length > 0 && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Selected Photos ({uploadedPhotos.length})
            </Typography>
            <Grid container spacing={2}>
              {uploadedPhotos.map((photo) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={photo.id}>
                  <Card
                    sx={{
                      position: "relative",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      opacity: photo.uploading ? 0.7 : 1,
                      "&:hover": {
                        transform: photo.uploading ? "none" : "scale(1.02)",
                        "& .photo-overlay": {
                          opacity: photo.uploading ? 0 : 1,
                        },
                      },
                    }}
                    onClick={() =>
                      !photo.uploading && setSelectedPhoto(photo.id)
                    }
                  >
                    <Box
                      sx={{
                        position: "relative",
                        paddingTop: "100%",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={photo.url}
                        alt={photo.name}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />

                      {/* Upload Progress Overlay */}
                      {photo.uploading && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: "rgba(0, 0, 0, 0.7)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <CircularProgress
                            size={32}
                            sx={{ color: "white", mb: 1 }}
                          />
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Uploading...
                          </Typography>
                          <Typography variant="caption">
                            {photo.progress}%
                          </Typography>
                        </Box>
                      )}

                      {/* Status Icon */}
                      {photo.uploaded && (
                        <Box
                          sx={{
                            display: "flex",
                            position: "absolute",
                            top: 8,
                            right: 8,
                            p: 0.5,
                            bgcolor: "success.main",
                            borderRadius: "50%",
                          }}
                        >
                          <CheckCircle sx={{ color: "white", fontSize: 16 }} />
                        </Box>
                      )}

                      {/* Action Overlay */}
                      {!photo.uploading && (
                        <Box
                          className="photo-overlay"
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: "rgba(0, 0, 0, 0.5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0,
                            transition: "opacity 0.2s",
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="View">
                              <IconButton sx={{ color: "white" }}>
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            {photo.uploaded && (
                              <Tooltip title="Download">
                                <IconButton
                                  sx={{ color: "white" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadPhoto(photo);
                                  }}
                                >
                                  <Download />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <IconButton
                                sx={{ color: "white" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePhoto(photo.id);
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <CardContent sx={{ p: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, mb: 0.5 }}
                      >
                        {photo.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {photo.uploadDate} • {photo.size}
                        {photo.uploaded && " • Uploaded"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>

      {/* Photo Detail Dialog */}
      <Dialog
        open={Boolean(selectedPhoto)}
        onClose={() => setSelectedPhoto(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedPhoto && (
            <Box>
              <img
                src={uploadedPhotos.find((p) => p.id === selectedPhoto)?.url}
                alt="Selected photo"
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PhotoUpload;
